import {
  isActionGroup,
  isRouteItem,
  isSelectableAction,
  RouteItem,
  SelectableAction,
  SelectableItem,
} from "shared-ts-ui-action";
import React, {
  DependencyList,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  NavigationItemObject,
  NavigationType,
  SelectedRouteItemResolver,
} from "shared-ts-ui-navigation";

const noDeps: DependencyList = [];

const updateSelectedRoute = (
  items: Array<NavigationItemObject>,
  selected: RouteItem | undefined
): Array<NavigationItemObject> => {
  return items.map((it) => {
    if (it === undefined) {
      return it;
    } else if (isRouteItem(it)) {
      if (selected !== undefined) {
        if (selected === it || selected.route === it.route) {
          return { ...it, selected: true };
        } else {
          return it.selected ? { ...it, selected: false } : it;
        }
      } else {
        return it.selected ? { ...it, selected: false } : it;
      }
    } else if (isActionGroup(it)) {
      return {
        ...it,
        items: updateSelectedRoute(it.items, selected),
      } as NavigationItemObject;
    } else {
      return it;
    }
  });
};

const updateSelectedAction = (
  items: Array<NavigationItemObject>,
  selected: SelectableAction | undefined
): Array<NavigationItemObject> => {
  return items.map((it) => {
    if (it === undefined) {
      return it;
    } else if (isSelectableAction(it)) {
      if (selected !== undefined) {
        if (selected === it || selected.name === it.name) {
          return { ...it, selected: true };
        } else {
          return it.selected ? { ...it, selected: false } : it;
        }
      } else {
        return it.selected ? { ...it, selected: false } : it;
      }
    } else if (isActionGroup(it)) {
      return {
        ...it,
        items: updateSelectedAction(it.items, selected),
      } as NavigationItemObject;
    } else {
      return it;
    }
  });
};

type UseNavigationReturnType = {
  navigationItems: Array<NavigationItemObject>;
  handleSelected: (item: SelectableItem) => void;
};
export const useNavigation = (
  initialItems: Array<NavigationItemObject>,
  deps: DependencyList,
  navigationType: NavigationType
): UseNavigationReturnType => {
  const resolvedDeps = deps ?? noDeps;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRouteItem, setSelectedRouteItem] = useState<
    RouteItem | undefined
  >(() =>
    new SelectedRouteItemResolver().resolve(initialItems, location.pathname)
  );
  const navigationItems = useMemo(() => {
    return updateSelectedRoute(initialItems, selectedRouteItem);
  }, [initialItems, selectedRouteItem, ...resolvedDeps]);

  // Update selected route item when location changes
  useEffect(() => {
    const newSelectedRouteItem = new SelectedRouteItemResolver().resolve(
      initialItems,
      location.pathname
    );
    if (newSelectedRouteItem?.route !== selectedRouteItem?.route) {
      setSelectedRouteItem(newSelectedRouteItem);
    }
  }, [location.pathname, initialItems, selectedRouteItem?.route]);



  const handleSelect = useCallback(
    (newSelectedItem: SelectableItem) => {
      if (isRouteItem(newSelectedItem)) {
        setSelectedRouteItem(newSelectedItem);
        navigate({ to: newSelectedItem.route });
      } else if (isSelectableAction(newSelectedItem)) {
        // For action items, we need to update the navigation items directly
        // since they don't affect the route-based selection
        setSelectedRouteItem((prev) => prev); // This triggers a re-render
      }
    },
    [navigate]
  );

  const filteredNavigationItems = useMemo(() => {
    let items = navigationItems;

    // Handle action selection separately from route selection
    if (navigationType === "rail") {
      items = items.filter((it) => !React.isValidElement(it));
    }

    return items;
  }, [navigationItems, navigationType]);

  return useMemo(
    () => ({
      navigationItems: filteredNavigationItems,
      handleSelected: handleSelect,
    }),
    [filteredNavigationItems, handleSelect]
  );
};
